import { useQuery } from '@tanstack/react-query';

import { GlobalSetting, GLOBAL_SETTING_KEYS } from 'types/global_setting';

import { API_BASE_URL } from 'services/api';

type UseGlobalSettingsParams = {
  keys?: string[];
  enabled?: boolean;
};

async function fetchGlobalSettings(
  keys?: string[]
): Promise<GlobalSetting[]> {
  const url = new URL(`${API_BASE_URL}/internal/global_settings`);

  // Add pagination parameter (required by the API)
  url.searchParams.append('pagination', 'offset');

  if (keys && keys.length > 0) {
    keys.forEach((key) => {
      url.searchParams.append('keys[]', key);
    });
  }

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error('Failed to fetch global settings');
  }

  const data = await response.json();

  // The endpoint can return either an array or a paginated response
  // Based on the OpenAPI spec, we'll handle both cases
  return Array.isArray(data) ? data : data.data || [];
}

export function useGlobalSettings(params?: UseGlobalSettingsParams) {
  const { keys, enabled = true } = params || {};

  return useQuery(
    ['global-settings', keys],
    () => fetchGlobalSettings(keys),
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes - settings don't change often
    }
  );
}

/**
 * Hook to check if Ring signups are disabled
 */
export function useIsRingSignupDisabled() {
  const { data: settings, isLoading } = useGlobalSettings({
    keys: [GLOBAL_SETTING_KEYS.DISABLE_RING_SIGNUP],
  });

  const disableRingSignup = settings?.find(
    (setting) => setting.key === GLOBAL_SETTING_KEYS.DISABLE_RING_SIGNUP
  );

  return {
    isDisabled: disableRingSignup?.value === 'true',
    isLoading,
  };
}
